import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';

export default function VoiceCostDialog({ request, sessionId, context, onClose }) {
    const [data, setData] = useState(null);
    const [rowId, setRowId] = useState('');
    const [field, setField] = useState('surveyor_assessment');
    const [reasons, setReasons] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const pending = useRef(false);
    const alive = useRef(false);
    const base = `/sessions/${sessionId}/costs`;
    useEffect(() => {
        alive.current = true;
        request(base).then((result) => { if (alive.current) setData(result); })
            .catch((failure) => { if (alive.current) setError(failure.message); });
        return () => { alive.current = false; };
    }, [request, base]);
    const perform = async (action) => {
        if (pending.current) return;
        pending.current = true; setBusy(true); setError(''); setMessage('');
        try {
            const text = await action();
            const refreshed = await request(base);
            if (alive.current) { setData(refreshed); setMessage(text); }
        } catch (failure) { if (alive.current) setError(failure.message); }
        finally { pending.current = false; if (alive.current) setBusy(false); }
    };
    const decide = (operation, decision) => perform(async () => {
        const result = await request(`${base}/${operation.id}/decision`, { method: 'POST', body: { decision, reason: reasons[operation.id]?.trim() } });
        if (result.state !== (decision === 'confirm' ? 'confirmed' : 'dismissed')) throw new Error('The cost decision was not acknowledged');
        return decision === 'confirm' ? 'Draft workbook updated. Review required. No revision published or funds released.' : 'Cost proposal dismissed. Workbook unchanged.';
    });
    return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>Cost Capture</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ pt: 1, overflowWrap: 'anywhere' }}>
            <Typography variant="body2">{context.project_reference} / {context.report_instance} / visit {context.visit_id}</Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {message && <Alert severity="success">{message}</Alert>}
            <TextField select label="Cost row" value={rowId} disabled={busy || !data} onChange={(event) => setRowId(event.target.value)}>
                {(data?.rows || []).map((row) => <MenuItem key={row.id} value={row.id} sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{row.description}</MenuItem>)}
            </TextField>
            <TextField select label="Cost field" value={field} disabled={busy} onChange={(event) => setField(event.target.value)}>
                <MenuItem value="surveyor_assessment">Surveyor assessment</MenuItem>
                <MenuItem value="borrower_budget">Borrower budget</MenuItem>
            </TextField>
            <Button startIcon={<SaveIcon />} disabled={busy || !rowId || !!context.read_only} onClick={() => perform(async () => {
                const result = await request(`${base}/selection`, { method: 'POST', body: { row_id: rowId, field, context_version: data.context_version } });
                if (!result.selected_cost) throw new Error('Cost cell selection was not acknowledged');
                return `Selected: ${result.selected_cost.description} / ${result.selected_cost.field}. Workbook unchanged.`;
            })}>Select cost cell</Button>
            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">Cost Proposals</Typography>
                <Tooltip title="Refresh cost proposals"><IconButton aria-label="Refresh cost proposals" disabled={busy} onClick={() => perform(async () => '')}><RefreshIcon /></IconButton></Tooltip>
            </Stack>
            {data && !data.proposals?.length && <Typography variant="body2">No cost proposals recorded.</Typography>}
            {(data?.proposals || []).map((operation) => <Stack key={operation.id} spacing={1} sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
                <Typography variant="subtitle2">{operation.proposal.description} / {operation.proposal.field}</Typography>
                <Typography variant="body2">Current: {operation.proposal.previous_value ?? 'Not recorded'}; proposed: {operation.proposal.currency} {operation.proposal.proposed_value}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{operation.proposal.source_text}</Typography>
                <Typography variant="caption">Status: {operation.proposal.state}. Draft only.</Typography>
                {operation.proposal.state === 'proposed' && <>
                    <TextField label="Cost decision reason" value={reasons[operation.id] || ''} disabled={busy} inputProps={{ maxLength: 1000 }}
                        onChange={(event) => setReasons({ ...reasons, [operation.id]: event.target.value })} />
                    <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                        <Button startIcon={<CheckIcon />} disabled={busy || !!context.read_only || !reasons[operation.id]?.trim()} onClick={() => decide(operation, 'confirm')}>Confirm draft cost</Button>
                        <Button startIcon={<BlockIcon />} disabled={busy || !!context.read_only || !reasons[operation.id]?.trim()} onClick={() => decide(operation, 'dismiss')}>Dismiss cost proposal</Button>
                    </Stack>
                </>}
            </Stack>)}
        </Stack></DialogContent>
        <DialogActions><Button disabled={busy} onClick={onClose}>Close</Button></DialogActions>
    </Dialog>;
}